import { Box, Flex } from 'reflexbox';
import { useTranslation } from 'react-i18next';

import * as graphql from 'generated/graphql';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';
import PersonAvatar from 'packages/kakapo-ui-kit/components/users/PersonAvatar';
import { SubSectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import Select from 'packages/kakapo-ui-kit/components/forms/controls/Select';
import DeleteButton from 'packages/kakapo-ui-kit/components/editors/DeleteButton';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import { IQueueJoinInput } from 'packages/kakapo-ui-application/pages/learning/components/LearningEventInviteAttendee';
import useAuthenticatedUser from 'packages/kakapo-ui-application/hooks/useAuthenticatedUser';
import useViewerRoles from 'packages/kakapo-ui-application/hooks/useUserRoles';

export default function LearningEventAttendee(props: {
	user: graphql.User;
	type: graphql.LearningEventAttendeeType;
	event?: graphql.LearningEvent;
	regular?: boolean;
	readonly?: boolean;
	queueJoin?: (input: IQueueJoinInput) => void;
	unqueueJoin?: (id: string) => void;
}) {
	const { t } = useTranslation();
	const authenticatedUser = useAuthenticatedUser();

	const roles = useViewerRoles();

	const options = [
		{
			label: t('learning.event.modal.host.type.hostOrganizer'),
			value: graphql.LearningEventAttendeeType.HostOrganizer,
		},
		{
			label: t('learning.event.modal.host.type.host'),
			value: graphql.LearningEventAttendeeType.Host,
		},
		{
			label: t('learning.event.modal.host.type.organizer'),
			value: graphql.LearningEventAttendeeType.Organizer,
		},
	];

	if (props.regular) {
		options.unshift({
			label: t('learning.event.modal.host.type.attendee'),
			value: graphql.LearningEventAttendeeType.Attendee,
		});
	}

	const value = options.find((option) => option.value === props.type);

	const [join, { loading: joinLoading }] = graphql.useJoinLearningEventMutation();
	const [leave, { loading: leaveLoading }] = graphql.useLeaveLearningEventMutation();

	const loading = joinLoading || leaveLoading;

	// Hide the leave button if:
	// - event doesn't exist yet and authenticated user is the attendee (creator can't be removed)
	// - event does exist and attendee is the creator (creator can't be removed)
	// - user doesn't have sufficient roles

	const hasWorkspaceRole = roles.isWorkspaceSuperuser || roles.isLearningOfficer;

	const hideLeaveButton =
		(!props.event && props.user.id === authenticatedUser.id) ||
		(props.event && props.user.id === props.event.createdBy.id) ||
		(!hasWorkspaceRole && props.event?.createdBy.id !== authenticatedUser.id);

	return (
		<>
			<Flex flexDirection="row" alignItems="center" my={2}>
				<Box mr={3}>
					<PersonAvatar person={props.user} />
				</Box>
				<Box flexGrow={1}>
					<SubSectionTitle>
						{props.user.firstName} {props.user.lastName}
					</SubSectionTitle>
				</Box>

				{props.readonly ? null : loading ? (
					<Spinner size="18px" isLoading verticalMargin={0} />
				) : (
					<>
						<Box alignSelf="center" ml={2} flex="240px" flexShrink={0} flexGrow={0} width={240}>
							<Select
								small
								unspaced
								value={value}
								options={options}
								onChange={(option) => {
									if (!props.event) {
										props.queueJoin!({
											...props.user,
											attendeeType: option!.value,
										});

										return;
									}

									join({
										variables: {
											input: {
												learningEventID: props.event!.id,
												userID: props.user.id!,
												attendeeType: option!.value,
											},
										},
									});
								}}
							/>
						</Box>
						<Box alignSelf="center" ml={4} width={28}>
							{!hideLeaveButton && (
								<DeleteButton
									onClick={() => {
										if (!props.event) {
											props.unqueueJoin!(props.user.id!);

											return;
										}

										leave({
											variables: {
												input: {
													learningEventID: props.event!.id,
													userID: props.user.id!,
												},
											},
										});
									}}
								/>
							)}
						</Box>
					</>
				)}
			</Flex>

			<HorizontalRule />
		</>
	);
}
